import axios from 'axios';
import { API_BASE_URL } from '../config';
import api from './middleware';

const API_URL = API_BASE_URL;

/**
 * Get all fixture sets
 * @returns {Promise<Array>} - List of fixture sets
 */
const getAllFixtures = async () => {
  try {
    const response = await api.get('/api/fixtures');
    return response.data;
  } catch (error) {
    console.error('Error getting fixtures:', error);
    throw error;
  }
};

/**
 * Get a specific fixture set by ID
 * @param {string} fixtureId - ID of the fixture to fetch
 * @returns {Promise<Object>} - Fixture details
 */
const getFixtureById = async (fixtureId) => {
  try {
    const response = await api.get(`/api/fixtures/${fixtureId}`);
    return response.data;
  } catch (error) {
    console.error(`Error getting fixture ${fixtureId}:`, error);
    throw error;
  }
};

/**
 * Get linked test suites for a fixture
 * @param {string} fixtureId - ID of the fixture
 * @returns {Promise<Array>} - List of linked test suites
 */
const getFixtureLinkedSuites = async (fixtureId) => {
  try {
    const response = await api.get(`/api/fixtures/${fixtureId}/links`);
    // Return the array directly, component will handle parsing
    return response.data;
  } catch (error) {
    console.error(`Error getting linked suites for fixture ${fixtureId}:`, error);
    throw error;
  }
};

/**
 * Creates a new fixture set
 * @param {string} name - Name of the fixture set
 * @param {string} description - Description of the fixture set
 * @param {Object} variables - Variables stored in the fixture set
 * @returns {Promise<Object>} - Created fixture set data
 */
const createFixtureSet = async (name, description, variables) => {
  try {
    const response = await api.post(
      `/api/fixtures`, 
      {
        name,
        description,
        variables
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating fixture set:', error);
    throw error;
  }
};

/**
 * Creates a fixture set and links it to test suites in one operation
 * @param {string} name - Name of the fixture set
 * @param {string} description - Description of the fixture set
 * @param {Object} variables - Variables stored in the fixture set
 * @param {Array} suiteLinks - List of test suites to link with default status
 * @returns {Promise<Object>} - Combined response with fixture set and link result
 */
const createAndLinkFixture = async (name, description, variables, suiteLinks) => {
  try {
    const response = await api.post(
      `/api/fixtures/create-and-link`,
      {
        fixture_data: {
          name,
          description,
          variables
        },
        suite_links: suiteLinks
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating and linking fixture:', error);
    throw error;
  }
};

/**
 * Links a fixture set to test suites
 * @param {string} fixtureSetId - ID of the fixture set to link
 * @param {Array} suiteLinks - List of test suites to link with default status
 * @returns {Promise<Object>} - Response with link status
 */
const linkFixtureToSuites = async (fixtureSetId, suiteLinks) => {
  try {
    const response = await api.post(
      `/api/fixtures/link`, 
      {
        fixture_set_id: fixtureSetId,
        suite_links: suiteLinks
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error linking fixture set:', error);
    throw error;
  }
};

/**
 * Updates an existing fixture set
 * @param {string} fixtureId - ID of the fixture to update
 * @param {string} name - Updated name of the fixture set
 * @param {string} description - Updated description of the fixture set
 * @param {Object} variables - Updated variables stored in the fixture set
 * @returns {Promise<Object>} - Updated fixture set data
 */
const updateFixture = async (fixtureId, name, description, variables) => {
  try {
    const response = await api.put(
      `/api/fixtures/${fixtureId}`, 
      {
        name,
        description,
        variables
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating fixture ${fixtureId}:`, error);
    throw error;
  }
};

/**
 * Deletes a fixture set
 * @param {string} fixtureId - ID of the fixture to delete
 * @returns {Promise<Object>} - Response with delete status
 */
const deleteFixture = async (fixtureId) => {
  try {
    const response = await api.delete(`/api/fixtures/${fixtureId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting fixture ${fixtureId}:`, error);
    throw error;
  }
};

/**
 * Creates a new test suite manifest YAML file
 * @param {string} yamlContent - YAML content for the manifest
 * @returns {Promise<Object>} - Created manifest data
 */
const createSuiteManifest = async (yamlContent) => {
  try {
    const response = await api.post(
      `/api/manifest`,
      {
        yaml_content: yamlContent
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error creating manifest:`, error);
    throw error;
  }
};

/**
 * Get manifest for the authenticated user
 * @returns {Promise<Object>} - Manifest data
 */
const getUserManifest = async () => {
  try {
    const response = await api.get(`/api/manifest`);
    
    // API returns an array of manifests for the user
    if (response.data && Array.isArray(response.data) && response.data.length > 0) {
      // Return the most recent manifest (assuming they're ordered by creation date)
      return response.data[0];
    }
    
    // If we get here, no manifest exists
    return null;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      // If manifest doesn't exist yet, return null
      return null;
    }
    console.error(`Error getting manifest:`, error);
    throw error;
  }
};

/**
 * Get a specific manifest by ID
 * @param {string} manifestId - ID of the manifest
 * @returns {Promise<Object>} - Manifest data
 */
const getManifestById = async (manifestId) => {
  try {
    const response = await api.get(`/api/manifest/${manifestId}`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      // If manifest doesn't exist yet, return null
      return null;
    }
    console.error(`Error getting manifest ${manifestId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing manifest
 * @param {string} manifestId - ID of the manifest to update
 * @param {string} yamlContent - Updated YAML content
 * @returns {Promise<Object>} - Updated manifest data
 */
const updateManifest = async (manifestId, yamlContent) => {
  try {
    const response = await api.put(
      `/api/manifest/${manifestId}`,
      {
        yaml_content: yamlContent
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating manifest ${manifestId}:`, error);
    throw error;
  }
};

/**
 * Deletes a manifest
 * @param {string} manifestId - ID of the manifest to delete
 * @returns {Promise<Object>} - Response with delete status
 */
const deleteManifest = async (manifestId) => {
  try {
    const response = await api.delete(`/api/manifest/${manifestId}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting manifest ${manifestId}:`, error);
    throw error;
  }
};

export const testDataApi = {
  getAllFixtures,
  getFixtureById,
  getFixtureLinkedSuites,
  createFixtureSet,
  linkFixtureToSuites,
  createAndLinkFixture,
  updateFixture,
  deleteFixture,
  createSuiteManifest,
  getUserManifest,
  getManifestById,
  updateManifest,
  deleteManifest,
  // Keep these for backward compatibility
  getSuiteManifest: getUserManifest,
  updateSuiteManifest: updateManifest
};
